import { MRT_ColumnDef } from 'mantine-react-table'
import { Text, } from '@mantine/core'
import { ReactNode } from 'react'
import LoanActions from '../loan-actions'
import StatusPill from '../../common/pill-status'
import { formatDateToShow, formatNumberToCurrency, stringToDate } from 'app/utils/util-functions'
import { LoanParams } from 'app/models/loan-params'
import { useSelector } from 'react-redux'
import { getIndexRateOptions } from 'app/state/ducks/index-rate-options/selectors'
import { NonProRataIconWithTooltip } from 'app/views/components/icons/NonProRataIconWithTooltip'

function TableHead({ header }: { header: string }) {
    return (
        <Text
            style={{
                fontFamily: 'Plus Jakarta Sans',
                fontSize: '14px',
                fontWeight: 700,
                lineHeight: '19px',
                color: '#413E3E',
            }}
        >
            {header}
        </Text>
    )
}

function TableRowText({ text }: { text: ReactNode }) {
    return (
        <Text
            style={{
                fontFamily: 'Plus Jakarta Sans',
                fontSize: '14px',
                fontWeight: 600,
                lineHeight: '19px',
                color: '#413E3E',
            }}
        >
            {text}
        </Text>
    )
}
export const columnDefs: MRT_ColumnDef<LoanParams>[] = [
    {
        size: 60,
        accessorKey: 'indexOption',
        header: 'Index Rate Option',
        Header: <TableHead header={'Index Rate Option'} />,
        Cell: ({ row }) => {
            const allIndexes = useSelector(getIndexRateOptions);
            const indexOption = allIndexes.find(index => index?.id === row.original.indexOption?.id)?.indexOption ?? '';
            return <TableRowText text={indexOption ?? ''} />;
        },
    },
    {
        size: 40,
        accessorKey: 'startDate',
        header: 'Start Date',
        Header: <TableHead header={'Start Date'} />,
        accessorFn: row => {
            return stringToDate(row.startDate);
        },
        mantineFilterTextInputProps: {
            placeholder: 'Date',
        },
        filterFn: 'lessThanOrEqualTo',
        filterVariant: 'date',
        sortingFn: 'datetime',
        Cell: ({ cell }) => {
            const formattedDate = formatDateToShow(cell.getValue<Date>());
            return <TableRowText text={formattedDate} />;
        },
    },
    {
        size: 40,
        accessorKey: 'endDate',
        header: 'End Date',
        Header: <TableHead header={'End Date'} />,
        accessorFn: row => {
            return stringToDate(row.endDate);
        },
        mantineFilterTextInputProps: {
            placeholder: 'Date',
        },
        filterFn: 'lessThanOrEqualTo',
        filterVariant: 'date',
        sortingFn: 'datetime',
        Cell: ({ cell }) => {
            const formattedDate = formatDateToShow(cell.getValue<Date>());
            return <TableRowText text={formattedDate} />;
        },
    },
    {
        size: 50,
        accessorKey: 'amount',
        header: 'Amount',
        Header: <TableHead header={'Amount'} />,
        Cell: ({ cell, row }: any) => {
            const value = cell?.getValue() ?? 0;
            const currency = row.original?.currency ?? 'USD';
            return (
                <TableRowText text={formatNumberToCurrency(value, currency)} />
            );
        },
    },
    {
        size: 30,
        accessorKey: 'allInRate',
        header: 'All In-Rate',
        Header: <TableHead header={'All In-Rate'} />,
        Cell: ({ row }) => {
            // For backwards compatibility: fallback to allInRate if allInRateWithPikPremium is not set
            const displayRate = Number(row.original.allInRateWithPikPremium ?? row.original.allInRate) || 0;
            return <TableRowText text={displayRate.toString()} />
        },
    },
    {
        size: 30,
        accessorKey: 'isNonProRata',
        header: 'Alerts',
        Header: <TableHead header={'Alerts'} />,
        enableColumnFilter: false,
        Cell: ({ row }) => {
            const loan = row.original
            const isNonProRata = loan.isNonProRata === true

            return (
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    {isNonProRata && <NonProRataIconWithTooltip tooltipText="Loan has non-prorata lender allocations" />}
                </div>
            )
        },
    },
    {
        size: 40,
        accessorKey: 'status',
        header: 'Status',
        Header: <TableHead header={'Status'} />,
        Cell: cell => {
            const status = cell.row.original.status ?? 'Draft'
            const amendmentType = cell.row.original.amendmentType
            const isAmendment = amendmentType === 'LoanEdit' || amendmentType === 'LoanAmendment'
            const additionalState = isAmendment ? 'Amendment' : ''

            return (
                <StatusPill
                    state={status}
                    additionalState={additionalState}
                />
            )
        },
    },
    {
        size: 40,
        accessorKey: 'id',
        header: 'Actions',
        Header: <TableHead header={'Actions'} />,
        enableColumnActions: false,
        enableColumnFilter: false,
        Cell: cell => <LoanActions fromOutside={false} item={cell.row.original} />,
    },
]
